import React, { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Mail, Send, CheckCircle2, XCircle, Clock } from "lucide-react";


function tierLabel(t) {
  if (t > 0) return { label: `Por vencer en ${t}d`, cls: "bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2]" };
  if (t === 0) return { label: "Vence hoy", cls: "bg-[#fef7ec] text-[#8a5500] border-[#f5c477]" };
  return { label: `${Math.abs(t)}d de atraso`, cls: "bg-[#fdecea] text-[#7a2514] border-[#d99584]" };
}


export default function CxcReminders() {
  const [candidatos, setCandidatos] = useState([]);
  const [sinEmail, setSinEmail] = useState(0);
  const [log, setLog] = useState([]);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirm, setConfirm] = useState(null); // {cliente_id?, tier?, force}
  const [loading, setLoading] = useState(false);

  const load = async () => {
    try {
      const [c, l] = await Promise.all([
        api.get("/cxc-reminders/candidatos"),
        api.get("/cxc-reminders/log"),
      ]);
      setCandidatos(c.data.clientes || []);
      setSinEmail(c.data.sin_email || 0);
      setLog(l.data || []);
    } catch (e) { toast.error(formatApiError(e)); }
  };
  useEffect(() => { load(); }, []);

  const enviarTodos = () => {
    setConfirm({ scope: "todos", n: candidatos.length });
    setConfirmOpen(true);
  };

  const enviarCliente = (c, tier) => {
    const ids = c.cxcs.filter(x => x.dias_al_vencimiento === tier).map(x => x.id);
    setConfirm({ scope: "cliente", cliente_nombre: c.cliente_nombre, tier, cxc_ids: ids, n: 1 });
    setConfirmOpen(true);
  };

  const confirmarEnvio = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (confirm.cxc_ids) payload.cxc_ids = confirm.cxc_ids;
      if (confirm.tier !== undefined) payload.dias_tier = confirm.tier;
      const { data } = await api.post("/cxc-reminders/enviar-ahora", payload);
      toast.success(`Recordatorios enviados: ${data.enviados} · Omitidos: ${data.omitidos}` +
                    (data.errores?.length ? ` · Errores: ${data.errores.length}` : ""));
      setConfirmOpen(false);
      load();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  const totalAdeudado = candidatos.reduce(
    (sum, c) => sum + c.cxcs.reduce((s, x) => s + (x.saldo || 0), 0), 0
  );

  return (
    <div className="space-y-6" data-testid="cxc-reminders-page">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#5c7368] mb-1">Contabilidad · Cobranza automática</div>
        <h1 className="text-3xl font-bold tracking-tight text-[#15241d]" style={{ fontFamily: "'Manrope', sans-serif" }}>Recordatorios a clientes</h1>
        <p className="text-sm text-[#4a5c53] mt-1 max-w-3xl">
          Envía correos automáticos desde <b>contabilidad@pharma-stern.com</b> a clientes con facturas
          próximas a vencer o vencidas. El scheduler se ejecuta <b>diariamente a las 14:00 UTC</b> —
          tiers: 14 / 7 / 3 / 0 días antes + 7 / 14 / 30 días de atraso. Cada tier se envía solo una vez
          por cliente para no saturar.
        </p>
      </div>

      <div className="flex flex-wrap gap-3 items-center p-4 bg-[#e9f2ee] border border-[#b2d0c2] rounded-md">
        <Mail className="text-[#205c45]" />
        <div className="flex-1">
          <div className="text-sm text-[#15241d]">
            <b>{candidatos.length}</b> cliente{candidatos.length !== 1 ? "s" : ""} con recordatorios pendientes ·
            Total adeudado <b className="tabular-nums">${totalAdeudado.toFixed(2)}</b>
          </div>
          <div className="text-xs text-[#5c7368] mt-0.5">
            Se enviará uno por tier (antispam 3 días)
            {sinEmail > 0 && <span className="ml-2 text-[#8a5500]">· ⚠️ {sinEmail} cliente{sinEmail !== 1 ? "s" : ""} sin email no recibirán recordatorio</span>}
          </div>
        </div>
        <Button
          className="bg-[#205c45]"
          disabled={candidatos.length === 0}
          onClick={enviarTodos}
          data-testid="btn-enviar-todos"
        >
          <Send size={14} className="mr-2" />Enviar ahora a todos
        </Button>
      </div>

      <Tabs defaultValue="candidatos">
        <TabsList className="bg-white border border-[#d1dbd6]">
          <TabsTrigger value="candidatos">Candidatos ({candidatos.length})</TabsTrigger>
          <TabsTrigger value="log">Historial de envíos</TabsTrigger>
        </TabsList>

        <TabsContent value="candidatos">
          <div className="mt-4 space-y-4">
            {candidatos.map(c => {
              const totalCliente = c.cxcs.reduce((s, x) => s + (x.saldo || 0), 0);
              return (
                <div key={c.cliente_id} className="border border-[#d1dbd6] bg-white rounded-md p-4">
                  <div className="flex items-start justify-between gap-3 mb-3">
                    <div>
                      <div className="font-semibold text-[#15241d]">{c.cliente_nombre}</div>
                      <div className="text-xs text-[#5c7368] mt-1">
                        {c.cxcs.length} factura{c.cxcs.length !== 1 ? "s" : ""} · Adeuda ${totalCliente.toFixed(2)}
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {c.tiers.map(t => {
                        const { label, cls } = tierLabel(t);
                        return (
                          <Button key={t} size="sm" variant="outline" className={`${cls} border`}
                                  onClick={() => enviarCliente(c, t)}
                                  data-testid={`enviar-${c.cliente_id}-${t}`}>
                            <Send size={12} className="mr-1" />{label}
                          </Button>
                        );
                      })}
                    </div>
                  </div>
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-left text-[#5c7368] uppercase tracking-wider">
                        <th className="pb-1">Factura</th>
                        <th className="pb-1">Vencimiento</th>
                        <th className="pb-1 text-right">Saldo</th>
                        <th className="pb-1">Tier</th>
                      </tr>
                    </thead>
                    <tbody>
                      {c.cxcs.map(x => {
                        const { label, cls } = tierLabel(x.dias_al_vencimiento);
                        return (
                          <tr key={x.id} className="border-t border-[#f1f5f3]">
                            <td className="py-1.5 font-mono">{x.numero_documento || x.numero || "—"}</td>
                            <td>{x.fecha_vencimiento}</td>
                            <td className="text-right tabular-nums font-semibold">${x.saldo?.toFixed(2)}</td>
                            <td><Badge className={`${cls} border`}>{label}</Badge></td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              );
            })}
            {candidatos.length === 0 && (
              <div className="text-center py-12 text-[#8ba397] border border-dashed border-[#d1dbd6] rounded-md bg-white">
                <CheckCircle2 size={40} className="mx-auto mb-2 text-[#205c45]" />
                <div>¡Al día! No hay clientes con facturas en los tiers de recordatorio.</div>
              </div>
            )}
          </div>
        </TabsContent>

        <TabsContent value="log">
          <div className="mt-4 rounded-md border border-[#d1dbd6] bg-white overflow-hidden">
            <table className="erp-table">
              <thead><tr><th>Fecha</th><th>Cliente</th><th>Email</th><th>Tier</th><th className="text-right">Total</th><th>#Fact</th><th>Estado</th></tr></thead>
              <tbody>
                {log.map((l, i) => (
                  <tr key={i}>
                    <td className="text-xs">{l.created_at?.slice(0, 16).replace("T", " ")}</td>
                    <td className="font-medium">{l.cliente_nombre}</td>
                    <td className="text-xs text-[#5c7368]">{l.email_to}</td>
                    <td><Badge className={`${tierLabel(l.tier).cls} border text-[10px]`}>{tierLabel(l.tier).label}</Badge></td>
                    <td className="text-right tabular-nums">${l.total_adeudado?.toFixed(2)}</td>
                    <td className="text-center">{l.n_facturas}</td>
                    <td>{l.ok
                      ? <Badge className="bg-[#e9f2ee] text-[#205c45] border-[#b2d0c2] border"><CheckCircle2 size={10} className="mr-1" />Enviado</Badge>
                      : <Badge className="bg-[#fdecea] text-[#7a2514] border-[#d99584] border" title={l.error}><XCircle size={10} className="mr-1" />Error</Badge>
                    }</td>
                  </tr>
                ))}
                {log.length === 0 && <tr><td colSpan={7} className="text-center py-10 text-[#8ba397]"><Clock className="inline mb-2" /><br />Sin envíos registrados aún</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirmar envío</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-[#15241d]">
            {confirm?.scope === "todos"
              ? <>Se enviarán recordatorios a los <b>{confirm.n} cliente{confirm.n !== 1 ? "s" : ""}</b> con saldos pendientes.</>
              : <>Se enviará recordatorio al cliente <b>{confirm?.cliente_nombre}</b> por el tier <b>{tierLabel(confirm?.tier || 0).label}</b>.</>}
          </p>
          <p className="text-xs text-[#5c7368]">Remitente: contabilidad@pharma-stern.com</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)} disabled={loading}>Cancelar</Button>
            <Button className="bg-[#205c45]" onClick={confirmarEnvio} disabled={loading} data-testid="confirm-enviar">
              {loading ? "Enviando…" : <>Confirmar y enviar</>}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
